import { tenant } from '@tenant';

export const CREATE_KEY_FORM_ID = 'create-key-form';

export const CREATE_KEY_MODAL_TEST_ID = 'developers_authentication_create';
export const CREATE_KEY_NAME_TEST_ID =
  'developers_authentication_create_name-input';
export const CREATE_KEY_METHOD_OPTION_TEST_ID = (method: AuthMethod) =>
  `developers_authentication_create_method_${method}-tab`;
export const CREATE_KEY_METHOD_INFO_TEST_ID =
  'developers_authentication_create_method-info';
export const CREATE_KEY_ENCRYPTION_TOGGLE_TEST_ID =
  'developers_authentication_create_encryption-checkbox';
export const CREATE_KEY_UPLOAD_TEST_ID =
  'developers_authentication_create_upload';
export const CREATE_KEY_UPLOAD_REMOVE_TEST_ID =
  'developers_authentication_create_upload-remove-btn';
export const CREATE_KEY_ACKNOWLEDGE_TEST_ID =
  'developers_authentication_create_acknowledge-checkbox';
export const CREATE_KEY_SUBMIT_TEST_ID =
  'developers_authentication_create_submit-btn';
export const CREATE_KEY_CANCEL_TEST_ID =
  'developers_authentication_create_cancel-btn';
export const CREATE_KEY_DOCS_LINK_TEST_ID =
  'developers_authentication_create_documentation-link';

export const CREATE_KEY_REVEAL_TEST_ID =
  'developers_authentication_create_reveal';
export const CREATE_KEY_REVEAL_UUID_TEST_ID =
  'developers_authentication_create_reveal_uuid-input';
export const CREATE_KEY_REVEAL_UUID_COPY_TEST_ID =
  'developers_authentication_create_reveal_uuid-copy-btn';
export const CREATE_KEY_REVEAL_SECRET_TEST_ID =
  'developers_authentication_create_reveal_secret-input';
export const CREATE_KEY_REVEAL_SECRET_COPY_TEST_ID =
  'developers_authentication_create_reveal_secret-copy-btn';
export const CREATE_KEY_REVEAL_DONE_TEST_ID =
  'developers_authentication_create_reveal_done-btn';

export const CREATE_KEY_TITLE = 'Create new key';
export const CREATE_KEY_DOCUMENTATION_LABEL = 'Documentation';
export const CREATE_KEY_DOCUMENTATION_URL = `${window.location.origin}/docs/guide/docs-integration#integration-authentication`;

export const CREATE_KEY_SUPPORT_PREFIX =
  'Need to share access with a third party? Contact support at ';
export const CREATE_KEY_SUPPORT_SUFFIX = ' to request a restricted API key.';

export const CREATE_KEY_NAME_LABEL = 'Name';
export const CREATE_KEY_NAME_PLACEHOLDER = 'e.g. Production API key';

export const CREATE_KEY_FULL_ACCESS_PREFIX = 'This key grants ';
export const CREATE_KEY_FULL_ACCESS_EMPHASIS = 'full access to all resources';
export const CREATE_KEY_FULL_ACCESS_SUFFIX = '.';

export const CREATE_KEY_METHOD_LABEL = 'Authentication method';
export const CREATE_KEY_METHOD_PKCS_LABEL = 'Certificate (PKCS7)';
export const CREATE_KEY_METHOD_JWT_LABEL = 'JWT (token-based)';
export const CREATE_KEY_METHOD_INFO_PKCS =
  'Certificate-based authentication enables end-to-end encryption. Upload a CSR to continue.';
export const CREATE_KEY_METHOD_INFO_PKCS_NO_ENCRYPTION =
  'Certificate-based authentication enables end-to-end encryption. With encrypted payloads off, no CSR is required.';
export const CREATE_KEY_METHOD_INFO_JWT = `Authenticate requests with JWTs you generate yourself. You sign tokens with your private key; ${tenant.name.company} verifies them using your public key.`;

export const CREATE_KEY_ENCRYPTION_LABEL = 'Encrypted payloads';
export const CREATE_KEY_ENCRYPTION_HELPER =
  'Enabled by default. Turn off for faster testing.';

export const CREATE_KEY_ACKNOWLEDGE_LABEL =
  'I understand this key grants full access to all resources';

export const CREATE_KEY_SUBMIT_LABEL = 'Create key';
export const CREATE_KEY_CANCEL_LABEL = 'Cancel';

export const CREATE_KEY_UPLOAD_DRAG_PREFIX = 'Drag your file(s) or ';
export const CREATE_KEY_UPLOAD_BROWSE = 'browse';
export const uploadedFilesLabel = (count: number) =>
  `Uploaded files (${count})`;

export const CREATE_KEY_FILE_TOO_LARGE = 'File exceeds the maximum size';
export const CREATE_KEY_FILE_WRONG_FORMAT = 'File type is not supported';
export const CREATE_KEY_GENERIC_ERROR = 'Something went wrong';

export const CREATE_KEY_REVEAL_TITLE = 'Key created';
export const CREATE_KEY_REVEAL_UUID_LABEL = 'X-Auth-UUID';
export const CREATE_KEY_REVEAL_SECRET_LABEL = 'Secret';
export const CREATE_KEY_REVEAL_SECRET_WARNING =
  "Copy your secret now — you won't be able to see it again.";
export const CREATE_KEY_REVEAL_DONE_LABEL = 'Done';

export const KILOBYTE = 1024;
const MEGABYTE = KILOBYTE * KILOBYTE;
export const JWT_MAX_UPLOAD_BYTES = 100 * KILOBYTE;
export const PKCS_MAX_UPLOAD_BYTES = MEGABYTE;

export type AuthMethod = 'pkcs' | 'jwt';

export type UploadConfig = {
  heading: string;
  accept: string;
  formatsLabel: string;
  maxBytes: number;
};

export const getUploadConfig = (method: AuthMethod): UploadConfig => {
  if (method === 'jwt') {
    return {
      heading: 'Upload your RSA public key',
      accept: '.pem,.pub,.key',
      formatsLabel: 'Accepted formats: .pem, .pub, .key (max 100 KB)',
      maxBytes: JWT_MAX_UPLOAD_BYTES
    };
  }

  return {
    heading: 'Upload your Certificate Signing Request (CSR)',
    accept: '.csr,.pem',
    formatsLabel: 'Accepted formats: .csr, .pem (max 1 MB)',
    maxBytes: PKCS_MAX_UPLOAD_BYTES
  };
};

export const isAcceptedFileFormat = (
  fileName: string,
  accept: string
): boolean => {
  const allowedExtensions = accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter((token) => token.startsWith('.'));

  const lowerName = fileName.toLowerCase();
  return allowedExtensions.some((extension) => lowerName.endsWith(extension));
};
